/* WOZ Merch — Variant image swap
 * Handles color-swatch clicks on collection tiles AND product pages.
 * Looks up the selected variant's featured image via Shopify's product.js
 * and swaps the currently-displayed image to match.
 */
(function () {
  'use strict';

  // Cache of product data by handle
  const cache = new Map();

  async function loadProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const p = fetch('/products/' + handle + '.js')
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    cache.set(handle, p);
    return p;
  }

  // Extract product handle from any URL/href
  function handleFromHref(href) {
    if (!href) return null;
    const m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // Given the input radio that was clicked, find sibling color/size inputs
  // in the same form and derive the selected variant.
  function selectedVariantFor(radioEl, product) {
    if (!product || !product.variants) return null;
    // Radios in the same group share `name`. Color radio has `name` containing 'color'.
    const form = radioEl.closest('form, .product-variants, [class*="variants"], [class*="product-form"]') || radioEl.parentElement;
    if (!form) return null;

    // Read all currently-checked variant-related radios/selects in this scope
    const opts = {};
    form.querySelectorAll('input[type="radio"]:checked, select').forEach(el => {
      // name pattern used by this theme: 'product-color-<sectionId>', 'product-size-<sectionId>'
      const n = (el.name || '').toLowerCase();
      let key = null;
      if (n.includes('color')) key = 'color';
      else if (n.includes('size')) key = 'size';
      else if (el.dataset && el.dataset.optionName) key = el.dataset.optionName.toLowerCase();
      if (key && el.value) opts[key] = el.value;
    });

    // Also include the just-clicked radio in case it isn't marked checked yet
    const clickedName = (radioEl.name || '').toLowerCase();
    if (clickedName.includes('color')) opts['color'] = radioEl.value;
    else if (clickedName.includes('size')) opts['size'] = radioEl.value;

    // Match against product.variants
    return product.variants.find(v => {
      const vOpts = (v.options || []).map(o => (o || '').toLowerCase());
      const vColor = vOpts[0]; // Color is option 1 by convention
      const vSize = vOpts[1];  // Size is option 2
      const wantColor = (opts.color || '').toLowerCase();
      const wantSize = (opts.size || '').toLowerCase();
      // Match color always; size only if we know it
      if (wantColor && vColor !== wantColor) return false;
      if (wantSize && vSize !== wantSize) return false;
      return true;
    }) || product.variants.find(v => {
      // Fallback: match color only
      const vColor = ((v.options || [])[0] || '').toLowerCase();
      return vColor === (opts.color || '').toLowerCase();
    });
  }

  // Locate the "primary image" element to swap for this radio's context.
  // Priority: nearest product card image, tile matched by handle, else PDP gallery.
  function findPrimaryImage(radioEl, handle) {
    // 1) same-scope card
    let scope = radioEl.closest('.product-item, .product-card, .card, [class*="product-item"], [class*="product-card"], [class*="grid__item"], li[class*="item"], article');
    if (scope) {
      const links = scope.querySelectorAll('a[href*="/products/"]');
      for (const l of links) {
        const img = l.querySelector('img');
        if (img && !img.src.includes('Woz_Logo')) { return { img: img, scope: scope, isTile: true }; }
      }
      const imgs = scope.querySelectorAll('img');
      for (const img of imgs) {
        if (!img.src.includes('Woz_Logo')) { return { img: img, scope: scope, isTile: true }; }
      }
    }
    // 2) tile matched by handle (works for detached quick-buy portals)
    if (handle) {
      const links = document.querySelectorAll('a[href*="/products/' + handle + '"]');
      for (const l of links) {
        const s = l.closest('.product-item, .product-card, [class*="product-item"], [class*="product-card"], [class*="grid__item"]');
        if (s) {
          const img = s.querySelector('a[href*="/products/"] img') || s.querySelector('img');
          if (img && !img.src.includes('Woz_Logo')) { return { img: img, scope: s, isTile: true }; }
        }
      }
    }
    // 3) PDP main gallery
    const galleryImg = document.querySelector('.product-gallery__media img, [class*="product-gallery"] img, [class*="product-media"] img, main .product img');
    if (galleryImg) { return { img: galleryImg, scope: document, isTile: false }; }
    return null;
  }

  // Swap the image src to the variant featured image.
  function applyImageSwap(target, featuredSrc) {
    if (!target || !featuredSrc) return;
    // Preserve size/query params from the current src
    const cur = target.img.getAttribute('src') || '';
    const qMatch = cur.match(/\?[^#]*$/);
    const q = qMatch ? qMatch[0] : '';
    // Normalize new src — Shopify variant featured_image can be relative
    let newSrc = featuredSrc;
    if (newSrc.startsWith('//')) newSrc = 'https:' + newSrc;
    // Keep the selected asset's version; carry over sizing only.
    const nextUrl = new URL(newSrc, location.origin);
    const sizing = new URLSearchParams(q);
    ['width', 'height', 'crop'].forEach(key => {
      if (sizing.has(key)) nextUrl.searchParams.set(key, sizing.get(key));
    });
    newSrc = nextUrl.href;
    if (newSrc === cur) return;
    target.img.src = newSrc;
    // Also update srcset if present so the browser doesn't reload the wrong one
    if (target.img.srcset) {
      target.img.srcset = '';
    }
  }

  function handleFromRadioId(id) {
    if (!id) { return null; }
    // Radio IDs look like 'linux-tee-option-color-3-...' or
    // 'linux-tee-option-color-3-quick_buy-...'. Everything before '-option-' is the handle.
    var i = id.indexOf('-option-');
    if (i > 0) { return id.substring(0, i); }
    return null;
  }

  async function onSwatchChange(radioEl) {
    try {
      // The native product-page gallery uses media IDs and handles variant
      // changes. Replacing its first image corrupts the Beige slide after a
      // Black/Blue selection. Keep this fallback for collection tiles only.
      const page = radioEl.closest('product-page');
      if (page && radioEl.closest('product-variants[data-main-product-variants]')) return;
      // 1) Preferred: derive handle directly from the radio's own id (works for
      //    quick-buy widgets rendered in portals detached from the product card).
      let handle = handleFromRadioId(radioEl.id);

      // 2) Fallback: walk up looking for an ancestor with a product link.
      if (!handle) {
        let node = radioEl;
        for (let i = 0; i < 10 && node; i++) {
          const link = node.querySelector && node.querySelector('a[href*="/products/"]');
          if (link) {
            handle = handleFromHref(link.getAttribute('href'));
            if (handle) { break; }
          }
          node = node.parentElement;
        }
      }

      // 3) Last resort: current PDP URL.
      if (!handle) { handle = handleFromHref(location.pathname); }
      if (!handle) { return; }

      const product = await loadProduct(handle);
      if (!product) return;

      const variant = selectedVariantFor(radioEl, product);
      if (!variant || !variant.featured_image || !variant.featured_image.src) return;

      const target = findPrimaryImage(radioEl, handle);
      if (!target || !target.isTile) return;

      applyImageSwap(target, variant.featured_image.src);
    } catch (e) {
      /* silent — never break the page */
    }
  }

  function isSwatchRadio(el) {
    if (!el) { return false; }
    if (el.tagName !== 'INPUT') { return false; }
    if (el.type !== 'radio') { return false; }
    var nm = (el.name || '').toLowerCase();
    if (nm.indexOf('color') !== -1) { return true; }
    if (nm.indexOf('size') !== -1) { return true; }
    return false;
  }

  // Listen at document level so it works for anything the theme lazy-renders.
  document.addEventListener('change', function (ev) {
    if (isSwatchRadio(ev.target)) {
      onSwatchChange(ev.target);
    }
  }, true);

  // Also on click (labels wrap the radios in this theme, and some themes
  // stopPropagation on the change event).
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t) { return; }
    var label = t.closest ? t.closest('label') : null;
    if (!label) { return; }
    var forId = label.getAttribute('for');
    var radio = null;
    if (forId) {
      radio = document.getElementById(forId);
    } else {
      radio = label.querySelector('input[type="radio"]');
    }
    if (!isSwatchRadio(radio)) { return; }
    // Delay one tick so `checked` is set before we read it.
    setTimeout(function () { onSwatchChange(radio); }, 0);
  }, true);

  // Variant elements and sliders can initialize in different orders. Once
  // loaded, align the gallery with the already-selected variant without
  // altering media sources, the selected variant, or cart data.
  function bindGallerySync() {
    document.querySelectorAll('product-page').forEach(page => {
      const variants = page.querySelector('product-variants[data-main-product-variants]');
      const gallery = page.querySelector('[data-js-product-gallery]');
      if (!variants || !gallery || page.dataset.wozGallerySync) return;
      page.dataset.wozGallerySync = 'true';
      const sync = () => requestAnimationFrame(() => {
        const variant = variants.currentVariant;
        if (!variant || !variant.featured_media) return;
        const media = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
        const slider = gallery.querySelector('css-slider');
        if (media && slider && slider.sliderEnabled) {
          slider.changeSlide(media.dataset.index, 'auto');
        }
      });
      variants.addEventListener('VARIANT_CHANGE', sync);
      const slider = gallery.querySelector('css-slider');
      if (slider) slider.addEventListener('ready', sync);
      window.addEventListener('load', sync, { once: true });
      sync();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindGallerySync, { once: true });
  } else bindGallerySync();
  document.addEventListener('shopify:section:load', bindGallerySync);
})();
